import { useMemo, useState } from "react";
import {
  applyAssociationImportPreview,
  buildAssociationImportPreview,
  getDefaultWorkbookSheet,
  getWorkbookSheetNames,
  loadAllClientesSocios,
  readWorkbookFromFile,
  requestAssociationReceivablesGeneration,
  undoAssociationImport,
} from "../../services/associadosImport";
import RelImportacaoNaoEncontrados from "../relatorios/importacao/rel_importacao_naoencontrados.jsx";
import RelImportacaoDesativados from "../relatorios/importacao/rel_importacao_desativados.jsx";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  return currencyFormatter.format(numericValue);
};

const getPreviewStatusLabel = (item) => {
  if (item.status === "matched") {
    return item.matchedBy ? `Conciliado por ${item.matchedBy}` : "Conciliado";
  }

  if (item.status === "ambiguous") {
    return "Conciliacao ambigua";
  }

  return "Nao encontrado";
};

const getPreviewActionLabel = (item) => {
  if (item.status === "not_found" && item.clientCreatePayload) {
    return "Criar cliente e vinculo";
  }

  if (item.status !== "matched") {
    return "Revisao manual";
  }

  if (item.socioCreatePayload) {
    return "Criar vinculo";
  }

  if (item.clientPatch || item.socioPatch) {
    return "Atualizar cadastro";
  }

  return "Manter ativo";
};

function ImportacaoDescontos({ onImportFinished }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [workbook, setWorkbook] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [lastImportResult, setLastImportResult] = useState(null);

  const sheetNames = useMemo(
    () => (workbook ? getWorkbookSheetNames(workbook) : []),
    [workbook],
  );

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setFeedback("");
    setPreview(null);
    setLastImportResult(null);

    if (!file) {
      setSelectedFile(null);
      setWorkbook(null);
      setSelectedSheet("");
      return;
    }

    try {
      const nextWorkbook = await readWorkbookFromFile(file);
      const defaultSheet = getDefaultWorkbookSheet(nextWorkbook);

      setSelectedFile(file);
      setWorkbook(nextWorkbook);
      setSelectedSheet(defaultSheet);
    } catch (error) {
      setSelectedFile(null);
      setWorkbook(null);
      setSelectedSheet("");
      setFeedback(
        error?.message || "Nao foi possivel ler o arquivo selecionado.",
      );
    }
  };

  const handlePreview = async () => {
    if (!workbook || !selectedSheet) {
      setFeedback("Selecione um arquivo e uma aba da planilha.");
      return;
    }

    setLoadingPreview(true);
    setFeedback("");
    setLastImportResult(null);

    try {
      const { clientes, socios } = await loadAllClientesSocios();
      const nextPreview = buildAssociationImportPreview({
        workbook,
        sheetName: selectedSheet,
        clientes,
        socios,
      });

      setPreview(nextPreview);
      setFeedback("Planilha analisada com sucesso.");
    } catch (error) {
      setPreview(null);
      setFeedback(
        error?.message || "Nao foi possivel preparar a importacao.",
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApply = async () => {
    if (!preview) {
      setFeedback("Analise a planilha antes de aplicar a importacao.");
      return;
    }

    const confirmed = window.confirm(
      "Confirma a aplicacao das alteracoes e desativacoes desta planilha?",
    );

    if (!confirmed) {
      return;
    }

    setApplying(true);
    setFeedback("");

    try {
      const result = await applyAssociationImportPreview(preview);
      let nextResult = result;
      const errorCount = result.errors.length;
      let nextFeedback = errorCount
        ? `Importacao concluida com ${errorCount} erro(s).`
        : "Importacao concluida com sucesso.";

      if (!errorCount) {
        const shouldGenerateReceivables = window.confirm(
          "Deseja gerar o contas a receber para os registros ativos que permaneceram no banco de dados?",
        );

        if (shouldGenerateReceivables) {
          try {
            const receivablesResult =
              await requestAssociationReceivablesGeneration(preview, result);

            nextResult = {
              ...result,
              rollbackPlan: {
                ...result.rollbackPlan,
                receivablesUndoToken: receivablesResult.undoToken || null,
              },
              receivables: receivablesResult,
            };

            nextFeedback = `${nextFeedback} Contas a receber enviado ao backend (${receivablesResult.created} gerado(s) e ${receivablesResult.cancelled} cancelado(s)).`;
          } catch (receivablesError) {
            nextFeedback = `${nextFeedback} A importacao foi aplicada, mas o envio da geracao do contas a receber falhou: ${receivablesError?.response?.data?.message || receivablesError?.message || "erro nao identificado"}.`;
          }
        }
      }

      setLastImportResult(nextResult);
      setFeedback(nextFeedback);

      if (typeof onImportFinished === "function") {
        onImportFinished();
      }
    } catch (error) {
      setFeedback(error?.message || "Falha ao aplicar a importacao.");
    } finally {
      setApplying(false);
    }
  };

  const handleUndo = async () => {
    if (!lastImportResult?.rollbackPlan) {
      return;
    }

    const confirmed = window.confirm(
      "Confirma a reversao da ultima importacao aplicada?",
    );

    if (!confirmed) {
      return;
    }

    setUndoing(true);
    setFeedback("");

    try {
      await undoAssociationImport(lastImportResult.rollbackPlan);
      setLastImportResult(null);
      setFeedback("Importacao revertida com sucesso.");

      if (typeof onImportFinished === "function") {
        onImportFinished();
      }
    } catch (error) {
      setFeedback(error?.message || "Falha ao reverter a importacao.");
    } finally {
      setUndoing(false);
    }
  };

  const importFinishedWithErrors = Boolean(lastImportResult?.errors?.length);

  return (
    <div className="card border-0 bg-light mb-4">
      <div className="card-body">
        <div className="row g-3 align-items-end">
          <div className="col-xl-4 col-md-12">
            <label className="form-label">Planilha mensal</label>
            <input
              type="file"
              className="form-control"
              accept=".xls,.xlsx"
              onChange={handleFileChange}
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <label className="form-label">Competencia / aba</label>
            <select
              className="form-control"
              value={selectedSheet}
              onChange={(event) => setSelectedSheet(event.target.value)}
              disabled={!sheetNames.length}
            >
              <option value=""></option>
              {sheetNames.map((sheetName) => (
                <option key={sheetName} value={sheetName}>
                  {sheetName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-xl-5 col-md-6 d-flex gap-2 flex-wrap">
            <button
              className="btn btn-outline-primary"
              onClick={handlePreview}
              disabled={!selectedFile || loadingPreview || applying || undoing}
            >
              {loadingPreview ? "Lendo planilha..." : "Analisar planilha"}
            </button>
            <button
              className="btn btn-success"
              onClick={handleApply}
              disabled={
                !preview ||
                loadingPreview ||
                applying ||
                undoing ||
                Boolean(lastImportResult)
              }
            >
              {applying ? "Aplicando..." : "Aplicar importacao"}
            </button>
            <button
              className="btn btn-outline-danger"
              onClick={handleUndo}
              disabled={!lastImportResult || applying || undoing}
            >
              {undoing ? "Revertendo..." : "Reverter ultima importacao"}
            </button>
          </div>
        </div>

        {feedback ? (
          <p
            className={`mt-3 mb-0 ${
              importFinishedWithErrors ? "text-warning" : "text-secondary"
            }`}
          >
            {feedback}
          </p>
        ) : null}

        {preview ? (
          <div className="mt-4">
            <div className="row g-3">
              <div className="col-xxl-2 col-xl-3 col-md-4">
                <div className="border rounded-3 bg-white p-3 h-100">
                  <small className="text-secondary d-block">Linhas</small>
                  <strong>{preview.summary.totalRows}</strong>
                </div>
              </div>
              <div className="col-xxl-2 col-xl-3 col-md-4">
                <div className="border rounded-3 bg-white p-3 h-100">
                  <small className="text-secondary d-block">Atualizacoes</small>
                  <strong>
                    {preview.summary.clientUpdates + preview.summary.socioUpdates}
                  </strong>
                </div>
              </div>
              <div className="col-xxl-2 col-xl-3 col-md-4">
                <div className="border rounded-3 bg-white p-3 h-100">
                  <small className="text-secondary d-block">
                    Serao incluidos
                  </small>
                  <strong>{preview.summary.includedRecords}</strong>
                </div>
              </div>
              <div className="col-xxl-2 col-xl-3 col-md-4">
                <div className="border rounded-3 bg-white p-3 h-100">
                  <small className="text-secondary d-block">
                    Serao desativados
                  </small>
                  <strong>{preview.summary.deactivations}</strong>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Previa da importacao</h6>
                <small className="text-secondary">
                  Exibindo ate 50 linhas da planilha analisada
                </small>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Linha</th>
                      <th>Nome</th>
                      <th>SIAPE</th>
                      <th>CPF</th>
                      <th>Orgao</th>
                      <th>Valor</th>
                      <th>Situacao</th>
                      <th>Acao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.previewItems.slice(0, 50).map((item) => (
                      <tr key={`${item.rowNumber}-${item.nome}-${item.cpf}`}>
                        <td>{item.rowNumber}</td>
                        <td>{item.nome || "-"}</td>
                        <td>{item.siape || "-"}</td>
                        <td>{item.cpf || "-"}</td>
                        <td>{item.orgao || "-"}</td>
                        <td>{formatMoney(item.valor)}</td>
                        <td>{getPreviewStatusLabel(item)}</td>
                        <td>{getPreviewActionLabel(item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {preview.notFoundItems.length ? (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    Registros nao encontrados que serao incluidos
                  </h6>
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-secondary">
                      Exibindo ate 20 registros
                    </small>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() =>
                        RelImportacaoNaoEncontrados(
                          preview.notFoundItems,
                          preview.sheetName,
                        )
                      }
                    >
                      Gerar PDF
                    </button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Nome</th>
                        <th>SIAPE</th>
                        <th>CPF</th>
                        <th>Orgao</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.notFoundItems.slice(0, 20).map((item) => (
                        <tr key={`not-found-${item.rowNumber}-${item.cpf || item.nome}`}>
                          <td>{item.rowNumber}</td>
                          <td>{item.nome || "-"}</td>
                          <td>{item.siape || "-"}</td>
                          <td>{item.cpf || "-"}</td>
                          <td>{item.orgao || "-"}</td>
                          <td>{formatMoney(item.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="6" className="fw-semibold">
                          Total de registros: {preview.notFoundItems.length}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : null}

            {preview.deactivationItems.length ? (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Clientes que serao desativados</h6>
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-secondary">
                      Exibindo ate 20 registros
                    </small>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() =>
                        RelImportacaoDesativados(
                          preview.deactivationItems,
                          preview.sheetName,
                        )
                      }
                    >
                      Gerar PDF
                    </button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Codigo</th>
                        <th>Nome</th>
                        <th>CPF</th>
                        <th>Valor atual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.deactivationItems.slice(0, 20).map((item) => (
                        <tr key={`deactivation-${item.client.cdcli}`}>
                          <td>{item.client.cdcli}</td>
                          <td>{item.client.decli}</td>
                          <td>{item.client.cnpj_cpfcli || "-"}</td>
                          <td>{formatMoney(item.client.vlrmensal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4" className="fw-semibold">
                          Total de registros: {preview.deactivationItems.length}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ImportacaoDescontos;
